/**
 * Typed calls, and the query keys that cache them.
 *
 * Keys include every parameter that changes the answer. A key that omits a filter
 * shows one filter's results under another's — which in a triage queue means an
 * analyst working a list that is not the list they asked for.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { request } from './client'
import type {
  Ancestry,
  EventRow,
  FindingDetail,
  FindingSummary,
  Page,
  Session,
} from './types'

// `| undefined` spelled out because `exactOptionalPropertyTypes` is on: clearing a
// filter sets it to undefined, and the compiler should agree that this is the same
// thing as not having it.
export interface QueueFilters {
  status?: string | undefined
  min_severity?: string | undefined
  hostname?: string | undefined
  rule_id?: string | undefined
  technique?: string | undefined
  assigned_to?: string | undefined
  tenant?: string | undefined
  page?: number | undefined
}

function search(filters: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value))
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: () => request<Session>('/auth/me/'),
    retry: false,
    staleTime: 60_000,
  })
}

export function useFindings(filters: QueueFilters) {
  return useQuery({
    queryKey: ['findings', filters],
    queryFn: () => request<Page<FindingSummary>>(`/findings/${search({ ...filters })}`),
    // Alerts arrive while somebody is looking at the queue. Thirty seconds is often
    // enough to feel live without making the list move under a cursor mid-triage.
    refetchInterval: 30_000,
  })
}

export function useFinding(id: string | undefined) {
  return useQuery({
    queryKey: ['finding', id],
    queryFn: () => request<FindingDetail>(`/findings/${id}/`),
    enabled: Boolean(id),
  })
}

export function useFindingContext(id: string | undefined, minutes = 30) {
  return useQuery({
    queryKey: ['finding-events', id, minutes],
    queryFn: () =>
      request<{ from: string; to: string; results: EventRow[] }>(
        `/findings/${id}/events/${search({ around_minutes: minutes })}`,
      ),
    enabled: Boolean(id),
    retry: false,
  })
}

export function useFindingAncestry(id: string | undefined, minutes = 60) {
  return useQuery({
    queryKey: ['finding-ancestry', id, minutes],
    queryFn: () =>
      request<Ancestry>(`/findings/${id}/ancestry/${search({ around_minutes: minutes })}`),
    enabled: Boolean(id),
    // Not retried, and deliberately separate from the alert itself: the tree is a
    // walk over the event store, and a slow store must not take the alert's own
    // story off the screen during the incident that made the store slow.
    retry: false,
  })
}

export function useCloseFinding() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; disposition: string; reason: string }) =>
      request<FindingDetail>(`/findings/${input.id}/close/`, {
        method: 'POST',
        body: { disposition: input.disposition, reason: input.reason },
      }),
    onSuccess: (finding) => {
      client.invalidateQueries({ queryKey: ['findings'] })
      client.setQueryData(['finding', finding.id], finding)
    },
  })
}

export function useAssignFinding() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; assignee: string }) =>
      request<FindingDetail>(`/findings/${input.id}/assign/`, {
        method: 'POST',
        body: { assignee: input.assignee },
      }),
    onSuccess: (finding) => {
      client.invalidateQueries({ queryKey: ['findings'] })
      client.setQueryData(['finding', finding.id], finding)
    },
  })
}

export interface HuntFilters {
  q?: string | undefined
  group_by?: string | undefined
  hostname?: string | undefined
  user?: string | undefined
  src_ip?: string | undefined
  since?: string | undefined
  until?: string | undefined
  limit?: number | undefined
  tenant?: string | undefined
}

export function useHunt(filters: HuntFilters, enabled: boolean) {
  return useQuery({
    queryKey: ['hunt', filters],
    queryFn: () => request<HuntResult>(`/events/${search({ ...filters })}`),
    enabled,
    retry: false,
  })
}

export interface HuntResult {
  from: string
  to: string
  took_ms?: number
  scanned_rows?: number | null
  scanned_bytes?: number | null
  count?: number
  truncated?: boolean
  results?: EventRow[]
  group_by?: string
  groups?: { value: string; count: number }[]
}

export function useHuntFields() {
  return useQuery({
    queryKey: ['event-fields'],
    queryFn: () => request<{ groupable: string[] }>('/events/fields/'),
    // The schema does not change while somebody is looking at it.
    staleTime: 5 * 60_000,
  })
}

export function useFieldValues(field: string, prefix: string, tenant?: string) {
  return useQuery({
    queryKey: ['event-facets', field, prefix, tenant],
    queryFn: () =>
      request<{ field: string; values: { value: string; count: number }[]; from: string }>(
        `/events/facets/${search({ field, prefix, tenant })}`,
      ),
    // Suggestions are a convenience. One that fails must not put an error where the
    // analyst is typing, so it is neither retried nor surfaced.
    enabled: Boolean(field),
    retry: false,
    staleTime: 30_000,
  })
}

export function useRules(tenant?: string) {
  return useQuery({
    queryKey: ['rules', tenant],
    queryFn: () =>
      request<
        {
          id: string
          rule_id: string
          rule_title: string
          status: string
          backtested_at: string | null
          backtest_window_days: number
          backtest_matches: number
          disabled_reason: string
        }[]
      >(`/rules/${search({ tenant })}`),
  })
}

export interface SavedRule {
  id: string
  rule_id: string
  title: string
  sigma: string
  source_query: Record<string, string>
  created_by: string
  created_at: string
}

export function useSavedRules(tenant?: string) {
  return useQuery({
    queryKey: ['saved-rules', tenant],
    queryFn: () => request<SavedRule[]>(`/rules/drafts/${search({ tenant })}`),
  })
}


/**
 * The response plane.
 *
 * Actions and playbooks are separate queries because they answer different
 * questions — "what has been done to my hosts" and "what is this platform allowed to
 * do without asking" — and an analyst usually wants one of them urgently and the
 * other never.
 */
export interface ResponseAction {
  id: string
  finding: string | null
  playbook_id: string
  step_index: number
  action: string
  target: string
  target_field: string
  identity: Record<string, string>
  hostname: string
  ttl_seconds: number
  status: string
  dry_run: boolean
  requested_by: string
  requested_at: string
  approval_roles: string[]
  approval_expires_at: string | null
  approved_by: string
  approved_at: string | null
  executed_at: string | null
  expires_at: string | null
  rolled_back_at: string | null
  rolled_back_by: string
  detail: string
  planned_commands: string[][]
  is_reversible: boolean
}

export interface PlaybookRow {
  id: string
  playbook_id: string
  playbook_name: string
  status: string
  acts: boolean
  dry_runs: number
  last_dry_run_at: string | null
  enabled_at: string | null
  enabled_by: string
  disabled_reason: string
  description?: string
  reference?: string
  missing?: boolean
  steps: {
    action: string
    target: string
    ttl_seconds: number
    approval_roles: string[]
    description: string
  }[]
}

export function useResponseActions(filters: { tenant?: string; status?: string } = {}) {
  return useQuery({
    queryKey: ['response-actions', filters],
    queryFn: () =>
      request<{ results: ResponseAction[] }>(`/response/actions/${search({ ...filters })}`),
    // An approval that nobody answers expires, so this screen has to move on its own
    // or somebody will be looking at a request that is no longer answerable.
    refetchInterval: 20_000,
  })
}

export function usePlaybooks(tenant?: string) {
  return useQuery({
    queryKey: ['playbooks', tenant],
    queryFn: () => request<{ results: PlaybookRow[] }>(`/response/playbooks/${search({ tenant })}`),
  })
}

export function useApproveAction(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string }) =>
      request<ResponseAction>(
        `/response/actions/${input.id}/approve/${search({ tenant })}`,
        { method: 'POST' },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['response-actions'] }),
  })
}

export function useRollbackAction(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      request<ResponseAction>(
        `/response/actions/${input.id}/rollback/${search({ tenant })}`,
        { method: 'POST', body: { reason: input.reason } },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['response-actions'] }),
  })
}

export function usePlaybookTransition(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; transition: string; reason?: string }) =>
      request<PlaybookRow>(
        `/response/playbooks/${input.id}/${input.transition}/${search({ tenant })}`,
        { method: 'POST', body: input.reason ? { reason: input.reason } : undefined },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['playbooks'] }),
  })
}

// ── inventory ──────────────────────────────────────────────────────────────

export interface InventoryFacet {
  facet: string
  item_count: number
  digest: string
  source: string
  supported: boolean
  truncated: boolean
  error: string
  stale: boolean
  collected_at: string | null
  changed_at: string | null
}

export interface InventoryHost {
  hostname: string
  facets: InventoryFacet[]
  unreadable: number
  stale: number
  last_collected_at: string | null
  last_changed_at: string | null
}

export interface InventoryItem {
  key: string
  value: string
  detail?: Record<string, string>
}

export interface InventoryFacetPage extends InventoryFacet {
  hostname: string
  matched: number
  offset: number
  limit: number
  items: InventoryItem[]
}

export interface DriftRow {
  id: string
  hostname: string
  facet: string
  change: 'added' | 'removed' | 'changed'
  item_key: string
  previous_value: string
  current_value: string
  severity: 'info' | 'notable' | 'critical'
  reason: string
  observed_at: string | null
  detected_at: string
  acknowledged_at: string | null
  acknowledged_by: string
}

export function useInventory(tenant?: string) {
  return useQuery({
    queryKey: ['inventory', tenant],
    queryFn: () => request<{ results: InventoryHost[] }>(`/inventory/${search({ tenant })}`),
  })
}

export function useInventoryFacet(
  hostname: string | undefined,
  facet: string | undefined,
  filters: { tenant?: string; q?: string; offset?: number } = {},
) {
  return useQuery({
    queryKey: ['inventory-facet', hostname, facet, filters],
    queryFn: () =>
      request<InventoryFacetPage>(
        `/inventory/${encodeURIComponent(hostname ?? '')}/${search({ facet, ...filters })}`,
      ),
    enabled: Boolean(hostname && facet),
  })
}

export function useDrift(filters: { tenant?: string; severity?: string; hostname?: string } = {}) {
  return useQuery({
    queryKey: ['drift', filters],
    queryFn: () => request<{ results: DriftRow[] }>(`/inventory/drift/${search({ ...filters })}`),
    // Inventory arrives hourly, so this does not need to be live. It does need to
    // stop showing a row somebody else has already acknowledged.
    refetchInterval: 120_000,
  })
}

export function useAcknowledgeDrift(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string }) =>
      request<DriftRow>(`/inventory/drift/${input.id}/acknowledge/${search({ tenant })}`, {
        method: 'POST',
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['drift'] }),
  })
}

// ── vulnerabilities ────────────────────────────────────────────────────────

export interface VulnRow {
  id: string
  hostname: string
  package: string
  installed_version: string
  fixed_version: string
  ecosystem: string
  assessed_as: string
  state: 'open' | 'resolved' | 'accepted' | 'no_fix'
  severity: 'unknown' | 'low' | 'medium' | 'high' | 'critical'
  cvss_score: number
  actionable: boolean
  cve: string
  osv_id: string
  summary: string
  cvss_vector: string
  references: string[]
  first_seen: string
  last_seen: string | null
  resolved_at: string | null
  resolved_version: string
  accepted_by: string
  accepted_reason: string
}

export interface VulnSummary {
  generated_at: string
  advisories_held: number
  outstanding: number
  by_severity: Record<string, number>
  by_state: Record<string, number>
  hosts_assessed: number
  hosts_unassessed: { hostname: string; reason: string }[]
  worst: {
    id: string
    hostname: string
    package: string
    installed_version: string
    fixed_version: string
    cve: string
    osv_id: string
    summary: string
    severity: string
    cvss_score: number
    state: string
  }[]
}

export function useVulnSummary(tenant?: string) {
  return useQuery({
    queryKey: ['vuln-summary', tenant],
    queryFn: () => request<VulnSummary>(`/vulnerabilities/summary/${search({ tenant })}`),
  })
}

export interface VulnFilters {
  tenant?: string | undefined
  state?: string | undefined
  severity?: string | undefined
  hostname?: string | undefined
  package?: string | undefined
  actionable?: string | undefined
}

export function useVulnerabilities(filters: VulnFilters) {
  return useQuery({
    queryKey: ['vulnerabilities', filters],
    queryFn: () =>
      request<{ count: number; results: VulnRow[] }>(`/vulnerabilities/${search({ ...filters })}`),
  })
}

export function useAcceptVulnerability(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      request<VulnRow>(`/vulnerabilities/${input.id}/accept/${search({ tenant })}`, {
        method: 'POST',
        body: { reason: input.reason },
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['vulnerabilities'] })
      client.invalidateQueries({ queryKey: ['vuln-summary'] })
    },
  })
}

// ── compliance ─────────────────────────────────────────────────────────────

export interface ComplianceRow {
  id: string
  hostname: string
  policy_id: string
  policy_title: string
  framework: string
  profile: string
  control_id: string
  control_title: string
  facet: string
  ecosystem: string
  status: 'pass' | 'fail' | 'unknown'
  severity: 'low' | 'medium' | 'high' | 'critical'
  detail: string
  evidence: { key: string; value: string }[]
  requirement: string
  rationale: string
  remediation: string
  references: string[]
  evaluated_at: string | null
}

export interface ComplianceGroup {
  hostname?: string
  policy_id?: string
  policy_title?: string
  passed: number
  failed: number
  unchecked: number
  decided: number
  score: number
}

export interface ComplianceSummary {
  score: number
  decided: number
  counts: Record<string, number>
  /** The sentence that stops the score being quoted on its own. */
  caveat: string
  hosts: ComplianceGroup[]
  policies: ComplianceGroup[]
  worst: {
    hostname: string
    policy_id: string
    control_id: string
    control_title: string
    severity: string
    detail: string
  }[]
}

export function useComplianceSummary(tenant?: string) {
  return useQuery({
    queryKey: ['compliance-summary', tenant],
    queryFn: () => request<ComplianceSummary>(`/compliance/summary/${search({ tenant })}`),
  })
}

export interface ComplianceFilters {
  tenant?: string | undefined
  hostname?: string | undefined
  status?: string | undefined
  severity?: string | undefined
}

export function useCompliance(filters: ComplianceFilters) {
  return useQuery({
    queryKey: ['compliance', filters],
    queryFn: () =>
      request<{ count: number; results: ComplianceRow[] }>(`/compliance/${search({ ...filters })}`),
  })
}

// ── reports ────────────────────────────────────────────────────────────────

export interface ReportRunRow {
  id: string
  kind: string
  title: string
  period_days: number
  figures: Record<string, string>
  blind_spots: string[]
  trigger: 'manual' | 'scheduled'
  generated_at: string
  generated_by: string
}

export interface ReportSection {
  heading: string
  narrative: string
  caveat: string
  figures: [string, string][]
  table: { headers: string[]; rows: string[][]; truncated_from: number } | null
}

export interface ReportDocument {
  kind: string
  title: string
  tenant: string
  generated_at: string
  period_days: number
  subtitle: string
  sections: ReportSection[]
  blind_spots: string[]
}

export interface ReportScheduleRow {
  id: string
  kind: string
  title: string
  cadence: 'daily' | 'weekly' | 'monthly'
  hour_utc: number
  period_days: number
  enabled: boolean
  created_by: string
  last_run_at: string | null
  last_error: string
}

export function useReportKinds() {
  return useQuery({
    queryKey: ['report-kinds'],
    queryFn: () => request<{ results: { kind: string; title: string }[] }>('/reports/kinds/'),
    staleTime: 3_600_000,
  })
}

export function useReportRuns(tenant?: string) {
  return useQuery({
    queryKey: ['report-runs', tenant],
    queryFn: () => request<{ results: ReportRunRow[] }>(`/reports/${search({ tenant })}`),
  })
}

export function useReportRun(id: string | undefined, tenant?: string) {
  return useQuery({
    queryKey: ['report-run', id, tenant],
    queryFn: () =>
      request<ReportRunRow & { document: ReportDocument }>(`/reports/${id}/${search({ tenant })}`),
    enabled: Boolean(id),
  })
}

export function useGenerateReport(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { kind: string }) =>
      request<ReportRunRow>(`/reports/${search({ tenant })}`, {
        method: 'POST',
        body: { kind: input.kind },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['report-runs'] }),
  })
}

export function useReportSchedules(tenant?: string) {
  return useQuery({
    queryKey: ['report-schedules', tenant],
    queryFn: () =>
      request<{ results: ReportScheduleRow[]; delivery: string }>(
        `/reports/schedules/${search({ tenant })}`,
      ),
  })
}

export function useScheduleReport(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { kind: string; cadence: string }) =>
      request<ReportScheduleRow>(`/reports/schedules/${search({ tenant })}`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['report-schedules'] }),
  })
}

/**
 * The download URL for a run.
 *
 * `as`, not `format`: DRF reserves `format` for content negotiation and answers 404
 * before the view runs. Built here so no caller has to know that.
 */
export function reportDownloadUrl(id: string, kind: 'pdf' | 'csv', tenant?: string): string {
  return `/api/v1/reports/${id}/download/${search({ as: kind, tenant })}`
}

// ── administration ─────────────────────────────────────────────────────────

export interface IntegritySummary {
  entries: number
  latest_sequence: number
  checkpoint: { sequence_no: number; created_at: string; unverified_since: number } | null
}

export interface IntegrityResult {
  intact: boolean
  entries_checked: number
  from_sequence: number
  to_sequence: number
  verified_from_checkpoint_at: string | null
  checkpoint_written: boolean
  divergence: {
    sequence_no: number
    reason: string
    expected: string
    actual: string
    summary: string
  } | null
  /** What "intact" does and does not prove, in words, from the server. */
  means: string
}

export interface AuditEntry {
  sequence_no: number
  action: string
  actor: string
  payload: Record<string, unknown>
  evidence: Record<string, unknown>
  created_at: string
  prev_hash: string
  row_hash: string
}

export interface MemberRow {
  id: string
  username: string
  role: 'read_only' | 'analyst' | 'responder' | 'admin'
  can_triage: boolean
  can_suppress: boolean
  can_administer: boolean
}

export interface HealthRow {
  name: string
  endpoint: string
  status: string
  detail: string
  latency_ms: number
  ok: boolean
}

export function useIntegrity(tenant?: string) {
  return useQuery({
    queryKey: ['integrity', tenant],
    queryFn: () => request<IntegritySummary>(`/admin/integrity/${search({ tenant })}`),
  })
}

export function useVerifyIntegrity(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () =>
      request<IntegrityResult>(`/admin/integrity/verify/${search({ tenant })}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['integrity'] })
      // The verification is itself an audited event, so the log below it has changed.
      client.invalidateQueries({ queryKey: ['audit'] })
    },
  })
}

export function useAuditLog(filters: { tenant?: string; action?: string } = {}) {
  return useQuery({
    queryKey: ['audit', filters],
    queryFn: () =>
      request<{ count: number; results: AuditEntry[] }>(`/admin/audit/${search({ ...filters })}`),
  })
}

export function useMembers(tenant?: string) {
  return useQuery({
    queryKey: ['members', tenant],
    queryFn: () => request<{ results: MemberRow[] }>(`/admin/members/${search({ tenant })}`),
  })
}

export function useSetMemberRole(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { username: string; role: string }) =>
      request<MemberRow>(`/admin/members/${search({ tenant })}`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['members'] })
      client.invalidateQueries({ queryKey: ['audit'] })
    },
  })
}

export function useSystemHealth(tenant?: string) {
  return useQuery({
    queryKey: ['system-health', tenant],
    queryFn: () =>
      request<{ results: HealthRow[]; degraded: string[] }>(`/admin/health/${search({ tenant })}`),
    // Probed live on the server; refetched often enough that the screen is not showing an
    // outage that ended, or hiding one that started.
    refetchInterval: 30_000,
  })
}

export interface RetentionSummary {
  policy: Record<string, number>
  meanings: Record<string, string>
  configured: boolean
  updated_by: string
  last_applied_at: string | null
  last_deleted: Record<string, number>
  /** What this policy refuses to touch, and why. Server-authored. */
  not_covered: { what: string; why: string }[]
  event_ttl_days: number
}

export interface RetentionRun {
  tenant: string
  dry_run: boolean
  deleted: Record<string, number>
  exempt: Record<string, number>
  total: number
}

export function useRetention(tenant?: string) {
  return useQuery({
    queryKey: ['retention', tenant],
    queryFn: () => request<RetentionSummary>(`/admin/retention/${search({ tenant })}`),
  })
}

export function useSetRetention(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: Record<string, number>) =>
      request<RetentionSummary>(`/admin/retention/${search({ tenant })}`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['retention'] })
      client.invalidateQueries({ queryKey: ['audit'] })
    },
  })
}

export function useApplyRetention(tenant?: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { confirm: boolean }) =>
      request<RetentionRun>(`/admin/retention/apply/${search({ tenant })}`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['retention'] })
      client.invalidateQueries({ queryKey: ['audit'] })
    },
  })
}
